/* fs-looks.js — pause logic for the Beautiful Drive looks rail.

   THIS FILE DOES NOT ANIMATE ANYTHING. The drift is a CSS keyframe on the
   compositor; all this does is decide when `.is-paused` is on the section, and
   CSS turns that into animation-play-state. Keeping the motion out of
   JavaScript is why a busy main thread cannot make the rail stutter.

   FOUR REASONS TO PAUSE, ORed together:
     hover     — a pointer is over the rail
     focus     — someone tabbed into it, so the focus ring must stop moving
     touch     — recently tapped, held for HOLD_MS afterwards
     offscreen — the section is not in view

   ⚠ THE TOUCH HOLD IS NOT DECORATION. Ezra asked for "pauses when touched",
   and on a phone there is no hover to pause it and no pointerleave to resume
   it. Releasing on touchend would restart the drift the instant a finger lifts,
   which is the moment someone starts reading the price they stopped it for.
   Every touch restarts the clock.

   ⚠ `visible` STARTS TRUE, NOT FALSE. If IntersectionObserver never delivers —
   no support, or a throttled tab — starting false leaves the rail permanently
   paused with no way back. Starting true means the worst case is a rail that
   drifts while off screen, which nobody sees.

   ===========================================================================
   ⚠ A FALSE TRAIL, RECORDED SO NOBODY FOLLOWS IT AGAIN (2026-09-06)
   ===========================================================================
   During review every one of the 16 images sat at `complete: false`,
   `naturalWidth: 0` after nine seconds, with correct src, correct srcset, and
   the first tile apparently in the viewport. Flipping one image to
   loading="eager" loaded it instantly. The obvious conclusion — that the
   composited, transform-animated ancestor suppresses lazy-load evaluation —
   was WRONG, and a hydrate-on-intersection "fix" was written and then deleted.

   The tab was simply not being rendered: document.visibilityState was
   "hidden", requestAnimationFrame delivered 0 frames in 2 seconds, and a
   freshly constructed IntersectionObserver never fired its initial callback at
   all. A hidden tab evaluates neither lazy-load visibility nor intersection
   observations. The eager flip "worked" only because eager fetches
   unconditionally — it was never evidence about ancestors.

   The deleted fix was strictly worse than nothing: it made images depend on an
   IntersectionObserver callback, so in the very situation that appeared to be
   the bug they would never have loaded at all, where plain loading="lazy"
   recovers by itself the moment the tab is shown.

   THE RULE: before diagnosing anything visual in an automated browser, check
   document.visibilityState and that rAF actually fires. Geometry, lazy-load
   and IntersectionObserver are all meaningless in a hidden tab — the same trap
   that produced a "483px closed / 0px open" accordion reading earlier in this
   project.
   =========================================================================== */
(function () {
  var HOLD_MS = 2500;

  function setup(root) {
    if (root.dataset.fsLooksReady) return;
    root.dataset.fsLooksReady = '1';

    var hover = false;
    var focus = false;
    var touch = false;
    var visible = true;
    var holdTimer = null;

    function apply() {
      var paused = hover || focus || touch || !visible;
      if (root.classList.contains('is-paused') !== paused) {
        root.classList.toggle('is-paused', paused);
      }
    }

    root.addEventListener('pointerenter', function (e) {
      /* A touch fires pointerenter too, and never fires pointerleave — which
         would leave the rail paused forever after one tap. Only a real hovering
         device sets the hover flag; touch has its own timer below. */
      if (e.pointerType === 'touch') return;
      hover = true;
      apply();
    });

    root.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'touch') return;
      hover = false;
      apply();
    });

    root.addEventListener('focusin', function () { focus = true; apply(); });
    root.addEventListener('focusout', function () { focus = false; apply(); });

    function hold() {
      touch = true;
      apply();
      if (holdTimer) clearTimeout(holdTimer);
      holdTimer = setTimeout(function () {
        touch = false;
        apply();
      }, HOLD_MS);
    }

    root.addEventListener('touchstart', hold, { passive: true });
    root.addEventListener('touchmove', hold, { passive: true });
    root.addEventListener('touchend', hold, { passive: true });

    /* This section sits below three full-height blocks, so on most visits it is
       off screen for the whole session. An unpaused compositor animation there
       costs battery on every phone that loads the homepage and buys nobody
       anything. */
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          visible = entries[i].isIntersecting;
        }
        apply();
      }, { rootMargin: '200px 0px' }).observe(root);
    }

    /* A backgrounded tab already suspends the animation; this makes the state
       honest so nothing resumes mid-loop looking like a skip. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        touch = false;
        if (holdTimer) clearTimeout(holdTimer);
      }
      apply();
    });

    apply();
  }

  function init() {
    var rails = document.querySelectorAll('[data-fs-looks]');
    for (var i = 0; i < rails.length; i++) setup(rails[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The customizer re-renders a section into a fresh DOM node, which never
     carries the ready flag — so this simply sets the new one up. */
  document.addEventListener('shopify:section:load', init);
})();
